import {
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { CreatomatePreview } from '@adflow/preview';
import {
  AdElement,
  AdElementImage,
  AdElementTeamAsset,
  AdElementText,
  AdElementVector,
  AdTemplate,
  Datasources,
  SoccerStages,
  appendSvgIfExtensionlessForCreatomate,
  buildCustomAssetContentUrl,
  isImageElement,
  isSVGElement,
  isTeamAssetElement,
  isTextElement,
  isVectorElement,
  parseElementName,
} from '@adflow/types';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Heading,
  Icon,
  ModalBody,
  ModalFooter,
  Stack,
  Text,
  Tooltip,
  UseRadioProps,
  useBoolean,
  useRadio,
  useRadioGroup,
} from '@chakra-ui/react';
import { FaShapes } from 'react-icons/fa';
import { GiAmericanFootballHelmet } from 'react-icons/gi';
import { LuImage, LuType } from 'react-icons/lu';
import { PiBoundingBox } from 'react-icons/pi';

import { EditorStoreContext } from '@adflow/editor';
import { UserContext } from '@adflow/utils';
import React from 'react';
import CreatomateDataMappingMagic from './CreatomateDataMappingMagic';
import { StepProps } from './';

export type StepTwoSubmit = (
  customElements: Array<AdElement>,
  dataSourceTypes?: Array<Datasources.SourceType>,
) => void;

interface StepTwoProps extends StepProps {
  onSubmit: StepTwoSubmit;
  elements: AdElement[];
  importSourceJSON: Exclude<AdTemplate['importSourceJSON'], undefined>;
}

const mapDataPointToTeamAssetSource = (
  dataPointName: string,
): 'HOME_ASSET' | 'AWAY_ASSET' | 'COMPETITOR_ASSET' | 'OPPONENT_ASSET' | undefined => {
  switch (dataPointName) {
    case 'HOME_JERSEY':
    case 'HOME_BADGE':
      return 'HOME_ASSET';
    case 'AWAY_JERSEY':
    case 'AWAY_BADGE':
      return 'AWAY_ASSET';
    case 'COMPETITOR_JERSEY':
    case 'COMPETITOR_BADGE':
      return 'COMPETITOR_ASSET';
    case 'OPPONENT_JERSEY':
    case 'OPPONENT_BADGE':
      return 'OPPONENT_ASSET';
    default:
      return undefined;
  }
};

const mapDataPointToDisplayAssetType = (dataPointName: string): 'JERSEY' | 'BADGE' | undefined => {
  switch (dataPointName) {
    case 'HOME_JERSEY':
    case 'AWAY_JERSEY':
    case 'COMPETITOR_JERSEY':
    case 'OPPONENT_JERSEY':
      return 'JERSEY';
    case 'HOME_BADGE':
    case 'AWAY_BADGE':
    case 'COMPETITOR_BADGE':
    case 'OPPONENT_BADGE':
      return 'BADGE';
    default:
      return undefined;
  }
};

const ComponentImportStepTwo: FC<PropsWithChildren<StepTwoProps>> = ({
  onClose,
  onSubmit,
  elements,
  importSourceJSON,
}) => {
  const ctx = useContext(EditorStoreContext);
  const { user } = useContext(UserContext);
  const [teamAssetElIds, setTeamAssetElIds] = useState<Array<string>>([]);
  const [soccerMomentsEnabled, setSoccerMomentsEnabled] = useState(false);
  const [stageOverride, setStageOverride] = useState<SoccerStages | null>(null);

  useEffect(() => {
    user?.hasFeature('cm-soccer-moments').then(setSoccerMomentsEnabled);
  }, [user]);

  const suggestedDataSources = useMemo(
    () =>
      Datasources.suggestDataSources([
        ...elements.filter(isTextElement),
        ...elements.filter(isImageElement),
      ]),
    [elements],
  );

  const inferredSoccerConfig = useMemo(
    () =>
      soccerMomentsEnabled ? Datasources.inferSoccerMomentsConfig(suggestedDataSources) : null,
    [suggestedDataSources, soccerMomentsEnabled],
  );

  const confirmedStage = stageOverride ?? inferredSoccerConfig?.stage;

  const filteredSuggestions = useMemo(() => {
    if (!soccerMomentsEnabled) {
      return Datasources.filterSoccerSuggestions(suggestedDataSources);
    }
    if (inferredSoccerConfig) {
      let filtered = Datasources.filterNonSoccerSportsSuggestions(suggestedDataSources);
      filtered = Datasources.filterNonSoccerTemplates(filtered);
      if (confirmedStage != null) {
        filtered = Datasources.filterToSoccerStage(filtered, confirmedStage);
      } else {
        filtered = Datasources.filterToSoccerCommonOnly(filtered);
      }
      return filtered;
    }
    return Datasources.filterSoccerSuggestions(suggestedDataSources);
  }, [suggestedDataSources, soccerMomentsEnabled, inferredSoccerConfig, confirmedStage]);

  const dataSourceTypes = useMemo(() => {
    return filteredSuggestions.map((ds) => {
      return ds[0].type;
    });
  }, [filteredSuggestions]);

  const [mappingEnabled, { toggle: toggleDataMapping }] = useBoolean(
    filteredSuggestions.length > 0,
  );

  const [checkedElements, setCheckedElements] = useState(
    [...elements]
      .map((el) => {
        return { selected: el.type === 'SVG', ...el };
      })
      .sort((a, b) => {
        if (a.selected && !b.selected) return -1;
        if (!a.selected && b.selected) return 1;
        return a.type.localeCompare(b.type) || a.name.localeCompare(b.name);
      })
      .map((el) => ({
        ...el,
        selected:
          filteredSuggestions.some((dps) => dps.some((dp) => dp.elId === el.id)) ||
          el.type === 'SVG',
      })),
  );

  // Re-check elements when filteredSuggestions updates (e.g. after soccer feature flag resolves)
  useEffect(() => {
    setCheckedElements((prev) =>
      prev.map((el) => ({
        ...el,
        selected:
          el.selected || filteredSuggestions.some((dps) => dps.some((dp) => dp.elId === el.id)),
      })),
    );
  }, [filteredSuggestions]);

  const lookupDataSource = useCallback(
    (elId: string) => {
      const matchingGroups = filteredSuggestions.filter((dps) =>
        dps.some((dp) => dp.elId === elId),
      );

      // When soccer moments is detected and multiple groups match, prefer soccer-type groups
      // over generic ones (e.g. prefer SOCCER/TEAM_HOME_COMPETITOR_ID over NEW_EVENT/HOME_COMPETITOR_ID
      // for HOME_CUSTOM elements, since both match via autoImportName aliases).
      const chosenGroup =
        inferredSoccerConfig && matchingGroups.length > 1
          ? matchingGroups.find((g) => Datasources.filterSoccerSuggestions([g]).length === 0) ??
            matchingGroups[0]
          : matchingGroups[0];

      const dataPoints = chosenGroup?.filter((dp) => dp.elId === elId);

      if (!dataPoints) {
        return {};
      }
      const dataPoint = dataPoints[0];
      const dataSource = ctx.dataSources.find((ds) => ds.type === dataPoint.type);
      if (!dataSource) {
        return {};
      }
      return { dataSource, dataPoint };
    },
    [ctx.dataSources, filteredSuggestions, inferredSoccerConfig],
  );

  const mapCustomImageElement = useCallback(
    (el: AdElementImage): AdElementImage => {
      const parsed = parseElementName(el.name);
      if (!mappingEnabled || !parsed.config?.customAssetConfig) return el;

      const { dataSource, dataPoint } = lookupDataSource(el.id);
      if (!dataSource || !dataPoint) return el;

      const contentUrl = buildCustomAssetContentUrl(
        {
          ...parsed.config.customAssetConfig,
          fileTemplate: appendSvgIfExtensionlessForCreatomate(
            parsed.config.customAssetConfig.fileTemplate,
          ),
        },
        dataPoint.dataPointName,
        dataSource.id,
      );

      return {
        ...el,
        data: {
          ...el.data,
          content: contentUrl,
        },
        sources: [
          ...el.sources,
          {
            sourceId: dataSource.id,
            name: dataPoint.dataPointName as Datasources.AllowableDataPoints,
          },
        ],
      };
    },
    [mappingEnabled, lookupDataSource],
  );

  const mapTeamAssetElement = useCallback(
    (el: AdElementImage) => {
      let autoImportOverrides = { data: {} };
      if (mappingEnabled) {
        const { dataSource, dataPoint } = lookupDataSource(el.id);
        if (dataSource && dataPoint) {
          autoImportOverrides = {
            data: {
              content: '',
              source: mapDataPointToTeamAssetSource(dataPoint.dataPointName),
            },
          };
        }
      }
      const val: AdElementTeamAsset = {
        ...el,
        type: 'TEAM_ASSET',
        data: {
          ...el.data,
          ...autoImportOverrides.data,
        },
      };
      return val;
    },
    [lookupDataSource, mappingEnabled],
  );

  const mapTextElement = useCallback(
    (el: AdElementText) => {
      let autoImportOverridesData = {};
      let autoImportOverridesSources: {
        sourceId: string;
        name: Datasources.AllowableDataPoints;
      }[] = [];

      if (mappingEnabled) {
        const { dataSource, dataPoint } = lookupDataSource(el.id);
        if (dataSource && dataPoint) {
          autoImportOverridesData = {
            content: `{${dataSource.id}:${dataPoint.dataPointName}}`,
          };
          autoImportOverridesSources = [
            {
              sourceId: dataSource.id,
              name: dataPoint.dataPointName as Datasources.AllowableDataPoints,
            },
          ];
        }
      }
      const val: AdElementText = {
        ...el,
        data: {
          ...el.data,
          ...autoImportOverridesData,
        },
        sources: [...el.sources, ...autoImportOverridesSources],
      };
      return val;
    },
    [lookupDataSource, mappingEnabled],
  );

  const getDisplayOptionsAssetType = useCallback(
    (elements: AdElement[]) => {
      const matches = elements.filter((el) => isTeamAssetElement(el));
      if (!matches) {
        return;
      }
      return matches
        .map((el) => {
          const { dataPoint } = lookupDataSource(el.id);
          if (dataPoint) {
            return mapDataPointToDisplayAssetType(dataPoint.dataPointName);
          }
        })
        .filter((x) => x)
        .pop();
    },
    [lookupDataSource],
  );

  const handleSubmit: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    async (evt) => {
      evt.preventDefault();
      const svgElements = elements.filter(isSVGElement);

      const filteredElements = checkedElements
        .filter((el) => el.selected)
        .map((el) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { selected, ...rest } = el;

          if (isTextElement(rest)) {
            return mapTextElement(rest);
          } else if (isImageElement(rest)) {
            const isTeamAsset = teamAssetElIds.includes(rest.id);
            if (isTeamAsset) return mapTeamAssetElement(rest);
            const parsed = parseElementName(rest.name);
            if (parsed.config?.customAssetConfig && mappingEnabled) {
              return mapCustomImageElement(rest);
            }
            return rest;
          } else if (isVectorElement(rest)) {
            return mapVectorElement(rest, svgElements[0].data.svg);
          } else {
            return rest;
          }
        });
      if (mappingEnabled) {
        const assetType = getDisplayOptionsAssetType(filteredElements);
        if (assetType) {
          ctx.store?.getState().setDisplayOptions('teamAssets', {
            type: assetType,
            fallback: 'NONE',
          });
        }
      }
      onSubmit(filteredElements, mappingEnabled ? dataSourceTypes : []);
    },
    [
      checkedElements,
      ctx.store,
      dataSourceTypes,
      elements,
      getDisplayOptionsAssetType,
      mapCustomImageElement,
      mapTeamAssetElement,
      mapTextElement,
      mappingEnabled,
      onSubmit,
      teamAssetElIds,
    ],
  );

  const onChangeHandle = useCallback(
    (inputIndex: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setCheckedElements(
        checkedElements.map((el, idx) => {
          if (inputIndex === idx) {
            return { ...el, selected: e.target.checked };
          }
          return el;
        }),
      );
    },
    [checkedElements],
  );

  useEffect(() => {
    if (!mappingEnabled) {
      return;
    }
    checkedElements.forEach((el) => {
      const match = filteredSuggestions.find((dps) => dps.some((dp) => dp.elId === el.id));
      if (!match) {
        return;
      }
      match.forEach((dp) => {
        if (mapDataPointToTeamAssetSource(dp.dataPointName)) {
          setTeamAssetElIds((prev) => [...prev.filter((id) => id !== dp.elId), dp.elId]);
        }
      });
    });
  }, [filteredSuggestions, checkedElements, mappingEnabled]);

  const handleToggleImgElType = (elId: string, newType: string) => {
    const elIsTeamAssetEl = teamAssetElIds.includes(elId);

    if (newType === 'teamasset' && !elIsTeamAssetEl) {
      setTeamAssetElIds((prev) => [...prev, elId]);
      return;
    }

    if (newType === 'img' && elIsTeamAssetEl) {
      setTeamAssetElIds((prev) => prev.filter((id) => id !== elId));
    }
  };

  const onToggleMapping = useCallback(
    (selected: boolean) => {
      setCheckedElements(
        checkedElements.map((el) => {
          const match = filteredSuggestions.find((dps) => dps.some((dp) => dp.elId === el.id));
          if (match) {
            return { ...el, selected: selected };
          }
          return el;
        }),
      );
      toggleDataMapping();
    },
    [checkedElements, toggleDataMapping, filteredSuggestions],
  );

  const getImageElementType = useCallback(
    (elId: string) => {
      if (!mappingEnabled) {
        return;
      }
      const match = filteredSuggestions.find((dps) => dps.some((dp) => dp.elId === elId));
      if (!match) {
        return;
      }
      return teamAssetElIds.includes(elId) ? 'teamasset' : 'img';
    },
    [filteredSuggestions, mappingEnabled, teamAssetElIds],
  );

  return (
    <>
      <ModalBody display="flex" alignItems="flex-start" columnGap={5}>
        <CreatomatePreview
          elements={elements}
          source={importSourceJSON}
          style={{
            width: '300px',
            height: '300px',
          }}
        ></CreatomatePreview>
        <Box flexDirection={'column'}>
          <Box
            borderWidth={1}
            borderRadius="md"
            px={4}
            py={3}
            flex={1}
            data-test-id="select-element"
          >
            <Heading as="h3" size="md">
              Element Selection
            </Heading>
            <Text size="md" mb={3}>
              Select any additional elements you would like to create.
            </Text>
            <Stack
              direction="column"
              maxH={'300px'}
              overflowY={'scroll'}
              data-test-id="elements-list"
            >
              {checkedElements.map((el, index) => {
                if (el.type === 'SVG') {
                  return (
                    <Checkbox pl={1} key={el.id} defaultChecked isDisabled>
                      <Box display={'flex'} alignItems="center">
                        <FaShapes /> <Text ml={2}>{el.name}</Text>
                      </Box>
                    </Checkbox>
                  );
                }
                if (el.type === 'TEXT') {
                  return (
                    <Checkbox
                      pl={1}
                      key={el.id}
                      isChecked={el.selected}
                      onChange={onChangeHandle(index)}
                    >
                      <Box display={'flex'} alignItems="center">
                        <Icon as={LuType}></Icon>
                        <Text ml={2} noOfLines={1} as="span">
                          {el.name}
                          <Text ml={2} fontStyle="italic" fontSize="xs" as="span">
                            - &quot;{el.data.content}&quot;
                          </Text>
                        </Text>
                      </Box>
                    </Checkbox>
                  );
                }
                if (el.type === 'IMAGE') {
                  return (
                    <Flex key={el.id} justifyContent="space-between">
                      <Checkbox pl={1} isChecked={el.selected} onChange={onChangeHandle(index)}>
                        <Box display={'flex'} alignItems="center">
                          {teamAssetElIds.includes(el.id) ? (
                            <GiAmericanFootballHelmet />
                          ) : (
                            <LuImage />
                          )}
                          <Text ml={2}>{el.name}</Text>
                        </Box>
                      </Checkbox>
                      <ImgElTypeRadioGroup
                        onChange={(type) => handleToggleImgElType(el.id, type)}
                        name={`${el.id}_assettype-toggle`}
                        val={getImageElementType(el.id)}
                      />
                    </Flex>
                  );
                }
                if (el.type === 'VECTOR') {
                  return (
                    <Checkbox
                      pl={1}
                      key={el.id}
                      isChecked={el.selected}
                      onChange={onChangeHandle(index)}
                    >
                      <Box display={'flex'} alignItems="center">
                        <PiBoundingBox />
                        <Text ml={2}>{el.name}</Text>
                      </Box>
                    </Checkbox>
                  );
                }
                return null;
              })}
            </Stack>
          </Box>
          <Box
            borderWidth={1}
            borderRadius="md"
            mt={2}
            px={4}
            py={3}
            flex={1}
            data-test-id="select-element"
          >
            <CreatomateDataMappingMagic
              mappingEnabled={mappingEnabled}
              onToggleMapping={onToggleMapping}
              suggestedDataSources={filteredSuggestions}
              soccerMomentsEnabled={soccerMomentsEnabled}
              inferredSoccerConfig={inferredSoccerConfig}
              selectedStage={confirmedStage}
              onStageOverride={setStageOverride}
            />
          </Box>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Button data-test-id="close-button" variant="ghost" mr={3} onClick={onClose}>
          Close
        </Button>
        <Button colorScheme="blue" onClick={handleSubmit} data-test-id="finish-selection">
          Finish
        </Button>
      </ModalFooter>
    </>
  );
};

ComponentImportStepTwo.displayName = 'ComponentImportStepTwo';

export default ComponentImportStepTwo;

const imgElOptions = [
  { value: 'img', label: 'Image' },
  { value: 'teamasset', label: 'Team Asset' },
];

const ImgElTypeRadioGroup: FC<{
  name: string;
  val?: 'img' | 'teamasset';
  onChange: (val: string) => void;
}> = ({ name, val, onChange }) => {
  const { getRootProps, getRadioProps, setValue } = useRadioGroup({
    name,
    defaultValue: 'img',
    onChange,
  });

  useEffect(() => {
    if (val) {
      setValue(val);
    }
  }, [setValue, val]);

  const group = getRootProps();

  return (
    <HStack {...group}>
      {imgElOptions.map(({ value, label }) => {
        const radio = getRadioProps({ value });
        return (
          <Tooltip label={label} key={value}>
            <Box>
              <RadioCard {...radio}>
                {value === 'img' ? <LuImage /> : <GiAmericanFootballHelmet />}
              </RadioCard>
            </Box>
          </Tooltip>
        );
      })}
    </HStack>
  );
};

const RadioCard: FC<PropsWithChildren<UseRadioProps>> = (props) => {
  const { getInputProps, getRadioProps } = useRadio(props);

  const input = getInputProps();
  const checkbox = getRadioProps();

  return (
    <Box as="label">
      <input {...input} />
      <Box
        {...checkbox}
        cursor="pointer"
        borderWidth="1px"
        borderRadius="md"
        boxShadow="md"
        _checked={{
          bg: 'teal.600',
          color: 'white',
          borderColor: 'teal.600',
        }}
        _focus={{
          boxShadow: 'outline',
        }}
        px={2}
        py={1}
      >
        {props.children}
      </Box>
    </Box>
  );
};

/**
 * This function takes in an AdElementVector and a string that contains a svg.
 * This svg contains all AdElements. We extract the Vector element and
 * effect filters. We also get the width and height from it and set it on the AdElementVector.
 * @param vectorElement
 * @param svgString
 * @returns AdElementVector with content, effectFilters and size(width, height) extracted from the svgString
 */
function mapVectorElement(vectorElement: AdElementVector, svgString: string): AdElementVector {
  const parser = new DOMParser();

  const svgDocument = parser.parseFromString(svgString, 'image/svg+xml');
  const vectorElementDoc = svgDocument.querySelector(
    '[id="' + vectorElement.name.replace(/["\\]/g, '\\$&').replace(/£/g, 'Â£') + '"]',
  );

  if (!vectorElementDoc) {
    return vectorElement;
  }
  const { width, height } = getWidthAndHeightFromSVGElement(svgDocument, vectorElement);

  const filterStr = getFilterStringFromSVGElement(svgDocument, vectorElement);
  return {
    ...vectorElement,
    data: {
      ...vectorElement.data,
      effectFilters: filterStr,
      content: vectorElementDoc.outerHTML,
      viewBox: {
        width,
        height,
      },
    },
  };
}

function getWidthAndHeightFromSVGElement(svgDocument: Document, vectorElement: AdElementVector) {
  const width =
    parseFloat(svgDocument.querySelector('svg')?.getAttribute('width') || '') ||
    vectorElement.size.width;
  const height =
    parseFloat(svgDocument.querySelector('svg')?.getAttribute('height') || '') ||
    vectorElement.size.height;
  return { width, height };
}

function getFilterStringFromSVGElement(svgDocument: Document, vectorElement: AdElementVector) {
  const groupElement = svgDocument.querySelector(
    '[id="' + vectorElement.name.replace(/["\\]/g, '\\$&').replace(/£/g, 'Â£') + '"]',
  );
  let filterStr = '';
  let filterId = '';
  filterId = groupElement?.getAttribute('filter')?.replace('url(#', '').replace(')', '') as string;
  let filterElement;
  if (filterId) {
    filterElement = svgDocument.querySelector(
      '[id="' + filterId.replace(/["\\]/g, '\\$&').replace(/£/g, 'Â£') + '"]',
    );
  }
  const serializer = new XMLSerializer();
  if (filterElement) {
    filterStr = serializer.serializeToString(filterElement);
  }
  return filterStr;
}

export const testExports = {
  mapDataPointToTeamAssetSource,
  mapDataPointToDisplayAssetType,
};
